import { useAlertStatus } from 'customHook/useAlertStatus';
import { useCallback } from 'react';
import { DealsService } from 'service/deals.service';
import { formatStageToAPI } from '../DealsDetails.utils';

export const useDealFieldUpdates = (
  dealData: any,
  setDealData: (updater: (prevData: any) => any) => void,
  ownerOptions: any[],
  getDeals?: () => void,
) => {
  const { completeOperation } = useAlertStatus();

  const updateDealField = useCallback(
    async (
      dealId: string | number,
      payload: any,
      optimisticUpdate: (prevData: any) => any,
      onSuccess?: () => void,
    ): Promise<boolean> => {
      const dealIdString = String(dealId);
      try {
        await DealsService.updateDeal(dealIdString, payload);

        setDealData(optimisticUpdate);

        setTimeout(async () => {
          try {
            const response = await DealsService.getDealById(dealIdString);

            const updatedData = response?.data || response;
            setDealData((prevData: any) => ({
              ...prevData,
              ...updatedData,
            }));

            if (onSuccess) {
              onSuccess();
            }
          } catch (refetchError) {
            console.error('Failed to refetch deal data:', refetchError);
          }
        }, 500);
        return true;
      } catch (error: any) {
        console.error('Failed to update deal:', error);
        completeOperation(
          'update',
          error?.response?.data?.message || 'Failed to update deal',
          true,
        );
        return false;
      }
    },
    [setDealData, completeOperation],
  );

  const handleDealStageNameChange = useCallback(
    async (newStage: string) => {
      if (!dealData?.id && !dealData?.dealId) {
        completeOperation('update', 'Deal ID not found', true);
        return;
      }

      const dealId = dealData.id || dealData.dealId;
      const targetStage = formatStageToAPI(newStage);
      const payload = { dealStageName: targetStage };

      await updateDealField(
        dealId,
        payload,
        (prevData: any) => ({
          ...prevData,
          dealStageName: targetStage,
        }),
        getDeals
          ? () => {
              getDeals();
            }
          : undefined,
      );
    },
    [dealData, updateDealField, completeOperation, getDeals],
  );

  const handleFieldChange = useCallback(
    async (fieldName: string, newValue: string) => {
      if (!dealData?.id && !dealData?.dealId) return;

      const dealId = dealData.id || dealData.dealId;
      const payload: any = {};

      const shouldRefetchDeals = fieldName === 'stageName' || fieldName === 'ownerName';

      if (fieldName === 'stageName') {
        const targetStage = newValue.toLowerCase();
        payload.stageName = targetStage;

        await updateDealField(
          dealId,
          payload,
          (prevData: any) => ({
            ...prevData,
            stageName: targetStage,
            stage: targetStage,
          }),
          shouldRefetchDeals && getDeals
            ? () => {
                getDeals();
              }
            : undefined,
        );
        return;
      }

      if (fieldName === 'ownerName') {
        payload.ownerName = newValue;
        const selectedOwner = ownerOptions.find((o) => o.value === newValue);
        if (selectedOwner) {
          payload.ownerId = selectedOwner.value;
        }
      } else {
        payload[fieldName] = newValue;
      }

      if (Object.keys(payload).length === 0) return;

      await updateDealField(
        dealId,
        payload,
        (prevData: any) => ({
          ...prevData,
          ...payload,
          [fieldName]: newValue,
        }),
        shouldRefetchDeals && getDeals
          ? () => {
              getDeals();
            }
          : undefined,
      );
    },
    [dealData, ownerOptions, updateDealField, getDeals],
  );

  return {
    handleDealStageNameChange,
    handleFieldChange,
  };
};
